import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { FIELDS_KEYS } from '_constants/forms';
import { getFieldProps } from '_helpers/forms/fields';

import * as Layout from '_components/atomic/layouts';
import BaseField from '_components/atomic/fields/BaseField';
import TextareaField from '_components/atomic/fields/TextareaField';
import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';

const { colors, devices, spaces } = theme;

const StyledContactUsForm = styled(Form)`
  max-width: ${spaces.large24X * 5 + 80}px;
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  padding: ${spaces.largeX + 2}px;
  background: #ffffff;
  box-shadow: 0px 30px 80px rgba(0, 0, 0, 0.1);
  border-radius: 30px;

  @media ${devices.tablet} {
    padding: ${spaces.regular}px;
    box-shadow: unset;
  }
`;

const SendButtonContainer = styled.div`
  text-align: right;

  @media ${devices.tablet} {
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ContactUsForm = ({ error, success, submitDisabled, onSubmit }) => {
  const {
    control,
    formState: { isValidating },
    reset,
    handleSubmit,
  } = useForm({
    reValidateMode: 'onBlur',
  });

  const [emailVerifying, setEmailVerifying] = useState(false);

  useEffect(() => {
    if (success) {
      reset();
    }
  }, [success, reset]);

  return (
    <StyledContactUsForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <BaseField {...getFieldProps(FIELDS_KEYS.fullName)} control={control} />
      <BaseField
        {...getFieldProps(FIELDS_KEYS.email, {
          validationOptions: { setProcessing: setEmailVerifying },
        })}
        control={control}
        loading={emailVerifying}
        feedback={emailVerifying ? 'Verifying...' : undefined}
      />
      <BaseField {...getFieldProps(FIELDS_KEYS.subject)} control={control} />
      <TextareaField
        {...getFieldProps(FIELDS_KEYS.message)}
        control={control}
      />
      <SendButtonContainer>
        <Button
          type="submit"
          large
          disabled={emailVerifying || isValidating || submitDisabled}
          color={theme.colors.indigo[600]}
          suffix={
            <Icon alias={ICONS_ALIASES.arrowRight} color={colors.white} />
          }
        >
          Send Message
        </Button>
      </SendButtonContainer>
      {success && (
        <>
          <Layout.Divider size={theme.spaces.medium} />
          <Layout.Card.Text color={theme.colors.gray[500]} bold noBottomSpace>
            Thank you for your question. Our agents will respond to your inquiry
            as quickly as possible.
          </Layout.Card.Text>
        </>
      )}
      {error && (
        <>
          <Layout.Divider size={theme.spaces.medium} />
          <Layout.Card.Text color={theme.colors.orange[500]} bold noBottomSpace>
            {error?.message}
          </Layout.Card.Text>
        </>
      )}
    </StyledContactUsForm>
  );
};

ContactUsForm.propTypes = {
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default ContactUsForm;
